import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Tab, Tree } from "@/comn/components";
import { useForm, useFetch, useStore, useToast, useModal, useTab, useTree, usePage, useResource } from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_ORG_MGMT_INFO,
    SF_ORG_DEPT_INFO,
} from "@/tra/tancis/ptli/usr/org/services/PtliOrgService";

/*
 * Organization Management Detail
 * !== 기관 관리 상세 조회 ==!
 */
export const PTLI0916004Q = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const tree = useTree(); // Tree Hook !== Tree Hook ==!
    const { orgCd } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    let tabIndex = 0;

    useResource({
        defaultSchema: [{ area: "orgDeptCd" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization Information Form !== 기관 정보 form ==!
        orgInfo: useForm({
            defaultSchema: SF_ORG_MGMT_INFO,
            defaultValues: {},
        }),
        // Organization Information Form !== 기관의 부서 정보 form ==!
        deptInfo: useForm({
            defaultSchema: SF_ORG_DEPT_INFO,
            defaultValues: {},
        }),
    };

    // Organization, Department Tab !== 기관,부서 탭 ==!
    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [{ label: "L_ORG" }, { label: "L_ORG_DEPT" }],
        },
    });

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check the list of departments in the Organization !== 기관의 부서 목록 조회 ==!
        getDeptList: useFetch({
            api: () => APIS.getOrgDeptList(orgCd),
            onSuccess: (data) => {
                const deptList = data["deptList"].content;
                tree.setData([
                    {
                        name: "ROOT",
                        deptPath: "ROOT",
                        children: handler.createTreeData(deptList, [], 1, ""),
                    },
                ]);

                tree.openTree();
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Check Organization Information !== 기관 정보 조회 ==!
        getOrgInfo: useFetch({
            api: (data) => APIS.getOrgInfo(orgCd),
            enabled: !!orgCd,
            onSuccess: (data) => {
                const orgInfo = data.orgInfo.content;
                form.orgInfo.setValues(orgInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Check Department Information !== 기관의 부서 정보 조회 ==!
        getDeptInfo: useFetch({
            api: (data) => APIS.getOrgDeptInfo(orgCd, data.deptCd),
            enabled: !!orgCd,
            onSuccess: (data) => {
                const deptInfo = data.deptInfo.content;
                form.deptInfo.setValues(deptInfo);
            },
            onError: (error) => {},
        }),
        // Delete Organization Information !== 기관 정보 삭제 ==!
        deleteOrgInfo: useFetch({
            api: (orgCodes) => APIS.deleteOrgInfo(orgCodes),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(URLS.ptli0916001q);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),

        // Delete Organization Information !== 기관의 부서 정보 삭제 ==!
        deleteOrgDeptInfo: useFetch({
            api: (datas) => APIS.deleteOrgDeptInfo(datas),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00061",
                    onCancel: () => {
                        navigate(URLS.ptli0916001q);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        initDeptForm: () => {
            form.deptInfo.setValue("deptPath", "ROOT");
        },
        // Delete Organization Information !== 기관 정보 삭제 ==!
        deleteOrgInfo: () => {
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteOrgInfo.fetch(orgCd);
                },
            });
        },

        // Delete Organization Department Information !== 기관의 부서 정보 삭제 ==!
        deleteOrgDeptInfo: () => {
            if (comnUtils.isEmpty(form.deptInfo.getValue("deptCd"))) {
                modal.openModal({ content: "msg.00004" }); // 선택된 데이터가 없습니다.
                return;
            }
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteOrgDeptInfo.fetch(form.deptInfo.getValues());
                },
            });
        },
        //Click Department Tree !== 부서 트리 클릭 ==!
        onTreeClick: (treeInfo: any) => {
            handler.initDeptForm();

            if (comnUtils.isEmpty(treeInfo.deptCd)) return;
            fetch.getDeptInfo.fetch(treeInfo);
        },
        //부서 목록 조회해서 데이터 뿌려주기
        createTreeData: (deptList: any, newList: any, level: any, deptCd: any) => {
            const tempList = deptList
                .map((item: any) => {
                    return {
                        name: comnUtils.replaceEmpty(item.deptNm),
                        checkbox: false,
                        deptCd: comnUtils.replaceEmpty(item.deptCd),
                        deptNm: comnUtils.replaceEmpty(item.deptNm),
                        deptLvl: comnUtils.replaceEmpty(item.deptLvl, 0),
                        deptIdtnOrd: comnUtils.replaceEmpty(item.deptIdtnOrd, 0),
                        uprDeptCd: comnUtils.replaceEmpty(item.uprDeptCd),
                        orgCd: comnUtils.replaceEmpty(item.orgCd),
                    };
                })
                .filter((item: any) => item["deptLvl"] === level && item["uprDeptCd"] === deptCd);
            //필터로 계층구조 필터링
            if (tempList.length === 0) return newList;

            tempList.forEach((item: any) => {
                if (!item.children) item.children = [];
                handler.createTreeData(deptList, item.children, item["deptLvl"] + 1, item["deptCd"]);
                newList.push(item);
            });

            return newList;
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgInfo.fetch();
        fetch.getDeptList.fetch();
        form.orgInfo.setEditable(false);
        form.deptInfo.setEditable(false);
        handler.initDeptForm();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Tab
                        {...tab.tab}
                        onChange={(index) => {
                            tabIndex = index;
                        }}
                    >
                        <Tab.Panel>
                            <Group.Section>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.orgRegnDvnCd}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.orgAbrtNm}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.orgNm}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.tin}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.hgovNm}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.orgStatCd}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.rprsTelno}></Group.Control>
                                    <Group.Control {...form.orgInfo.schema.rprsFaxNo}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.lpcoIssuYn}></Group.Control>
                                </Group.Row>
                                <Group.Row>
                                    <Group.Control {...form.orgInfo.schema.postAddr}></Group.Control>
                                </Group.Row>
                            </Group.Section>
                        </Tab.Panel>
                        <Tab.Panel>
                            <Layout gap={4}>
                                <Group>
                                    <Group.Body>
                                        <Tree {...tree.tree} size={12} height={300} onClick={handler.onTreeClick} />
                                    </Group.Body>
                                </Group>
                                <Group>
                                    <Group.Body>
                                        <Group.Section>
                                            <Group.Row>
                                                <Group.Control {...form.deptInfo.schema.uprDeptCd}></Group.Control>
                                            </Group.Row>
                                            <Group.Row>
                                                <Group.Control {...form.deptInfo.schema.deptNm}></Group.Control>
                                            </Group.Row>
                                            {/* <Group.Row>
                                                <Group.Control {...form.deptInfo.schema.orgStatCd}></Group.Control>
                                            </Group.Row> */}
                                            <Group.Row>
                                                <Group.Control {...form.deptInfo.schema.deptIdtnOrd}></Group.Control>
                                            </Group.Row>
                                            <Group.Row>
                                                <Group.Control {...form.deptInfo.schema.deptDesc}></Group.Control>
                                            </Group.Row>
                                            <Group.Row>
                                                <Group.Control {...form.deptInfo.schema.useYn}></Group.Control>
                                            </Group.Row>
                                        </Group.Section>
                                    </Group.Body>
                                </Group>
                            </Layout>
                        </Tab.Panel>
                    </Tab>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0916001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="delete"
                        onClick={() => {
                            if (tabIndex == 0) {
                                handler.deleteOrgInfo();
                            } else {
                                handler.deleteOrgDeptInfo();
                            }
                        }}
                    ></Button>
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.ptli0916003s}/${orgCd}`);
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
